/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import annualReturnDataService from "services/annualReturn";

// Async thunk for getting all annual returns
export const getAllAnnualReturnsAsync = createAsyncThunk(
  "annualReturns/getAll",
  async ({ queryParams }, { rejectWithValue }) => {
    try {
      const res = await annualReturnDataService.getAll(queryParams);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for creating an annual return
export const createAnnualReturnAsync = createAsyncThunk(
  "annualReturns/create",
  async ({ data }, { rejectWithValue }) => {
    try {
      const res = await annualReturnDataService.create(data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for updating an annual return
export const updateAnnualReturnAsync = createAsyncThunk(
  "annualReturns/update",
  async ({ annualReturnId, data }, { rejectWithValue }) => {
    try {
      const res = await annualReturnDataService.update(annualReturnId, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for getting a single annual return
export const getAnnualReturnAsync = createAsyncThunk(
  "annualReturns/get",
  async ({ annualReturnId }, { rejectWithValue }) => {
    try {
      const res = await annualReturnDataService.get(annualReturnId);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for deleting an annual return
export const deleteAnnualReturnAsync = createAsyncThunk(
  "annualReturns/delete",
  async ({ annualReturnId }, { rejectWithValue }) => {
    try {
      const res = await annualReturnDataService.remove(annualReturnId);
      return res.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  annualReturns: {},
  getAllAnnualReturnsStatus: {
    status: "idle",
    error: false,
    data: null,
  },
  createAnnualReturnStatus: {
    status: "idle",
    error: false,
    data: null,
  },
  updateAnnualReturnStatus: {
    status: "idle",
    error: false,
    data: null,
  },
  getAnnualReturnStatus: {
    status: "idle",
    error: false,
    data: null,
  },
  deleteAnnualReturnStatus: {
    status: "idle",
    error: false,
    data: null,
  },
};

const annualReturnsSlice = createSlice({
  name: "annualReturns",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllAnnualReturnsAsync.pending, (state) => {
        state.getAllAnnualReturnsStatus.status = "loading";
        state.getAllAnnualReturnsStatus.error = false;
        state.getAllAnnualReturnsStatus.data = null;
      })
      .addCase(getAllAnnualReturnsAsync.fulfilled, (state, action) => {
        state.getAllAnnualReturnsStatus.status = "idle";
        state.getAllAnnualReturnsStatus.error = false;
        state.getAllAnnualReturnsStatus.data = action.payload;
        state.annualReturns = action.payload;
      })
      .addCase(getAllAnnualReturnsAsync.rejected, (state, action) => {
        state.getAllAnnualReturnsStatus.status = "failed";
        state.getAllAnnualReturnsStatus.error =
          action.payload.messageText || "Failed to fetch annual returns";
      })
      .addCase(createAnnualReturnAsync.pending, (state) => {
        state.createAnnualReturnStatus.status = "loading";
        state.createAnnualReturnStatus.error = false;
        state.createAnnualReturnStatus.data = null;
      })
      .addCase(createAnnualReturnAsync.fulfilled, (state, action) => {
        state.createAnnualReturnStatus.status = "idle";
        state.createAnnualReturnStatus.error = false;
        state.createAnnualReturnStatus.data = action.payload;
        state.annualReturns = action.payload;
      })
      .addCase(createAnnualReturnAsync.rejected, (state, action) => {
        state.createAnnualReturnStatus.status = "failed";
        state.createAnnualReturnStatus.error =
          action.payload.messageText || "Failed to create annual return";
      })
      .addCase(updateAnnualReturnAsync.pending, (state) => {
        state.updateAnnualReturnStatus.status = "loading";
        state.updateAnnualReturnStatus.error = false;
        state.updateAnnualReturnStatus.data = null;
      })
      .addCase(updateAnnualReturnAsync.fulfilled, (state, action) => {
        state.updateAnnualReturnStatus.status = "idle";
        state.updateAnnualReturnStatus.error = false;
        state.updateAnnualReturnStatus.data = action.payload;
        state.annualReturns = { ...state.annualReturns, ...action.payload };
      })
      .addCase(updateAnnualReturnAsync.rejected, (state, action) => {
        state.updateAnnualReturnStatus.status = "failed";
        state.updateAnnualReturnStatus.error =
          action.payload.messageText || "Failed to update annual return";
      })
      .addCase(getAnnualReturnAsync.pending, (state) => {
        state.getAnnualReturnStatus.status = "loading";
        state.getAnnualReturnStatus.error = false;
        state.getAnnualReturnStatus.data = null;
      })
      .addCase(getAnnualReturnAsync.fulfilled, (state, action) => {
        state.getAnnualReturnStatus.status = "idle";
        state.getAnnualReturnStatus.error = false;
        state.getAnnualReturnStatus.data = action.payload;
        state.annualReturns = action.payload;
      })
      .addCase(getAnnualReturnAsync.rejected, (state, action) => {
        state.getAnnualReturnStatus.status = "failed";
        state.getAnnualReturnStatus.error =
          action.payload.messageText || "Failed to fetch annual return";
      })
      .addCase(deleteAnnualReturnAsync.pending, (state) => {
        state.deleteAnnualReturnStatus.status = "loading";
        state.deleteAnnualReturnStatus.error = false;
        state.deleteAnnualReturnStatus.data = null;
      })
      .addCase(deleteAnnualReturnAsync.fulfilled, (state, action) => {
        state.deleteAnnualReturnStatus.status = "idle";
        state.deleteAnnualReturnStatus.error = false;
        state.deleteAnnualReturnStatus.data = action.payload;
        delete state.annualReturns[action.payload._id];
      })
      .addCase(deleteAnnualReturnAsync.rejected, (state, action) => {
        state.deleteAnnualReturnStatus.status = "failed";
        state.deleteAnnualReturnStatus.error =
          action.payload.messageText || "Failed to delete annual return";
      });
  },
});

export default annualReturnsSlice.reducer;
